import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Users, Sparkles, Mail, Loader2, Trash2, Search, MessageSquarePlus, Handshake, GripVertical, Settings2, ArrowUp, ArrowDown, X, LayoutGrid, List, Pencil, ArrowUpDown, Phone, CheckSquare, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, PageLoader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";

const EMPTY = { name: "", email: "", phone: "", company: "", type: "lead", status: "new", tags: [], notes: "" };

const STAGE_COLORS = ["bg-slate-400", "bg-blue-500", "bg-amber-500", "bg-violet-500", "bg-cyan-500", "bg-pink-500"];
const stageColor = (stage, idx) =>
  stage.type === "won" ? "bg-accent" : stage.type === "lost" ? "bg-rose-500" : STAGE_COLORS[idx % STAGE_COLORS.length];
const TYPE_OPTIONS = [
  { value: "open", label: "In progress" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

export default function CRM() {
  const [params] = useSearchParams();
  const tab = params.get("tab") === "opportunities" ? "pipeline" : "contacts";
  return (
    <div>
      <PageHeader title="Clients & Prospects" subtitle="Your people and the opportunities in motion." testId="crm-header" />
      <Tabs defaultValue={tab}>
        <TabsList className="mb-6" data-testid="crm-tabs">
          <TabsTrigger value="contacts"><Users className="mr-1.5 h-4 w-4" /> Contacts</TabsTrigger>
          <TabsTrigger value="pipeline"><Handshake className="mr-1.5 h-4 w-4" /> Opportunities</TabsTrigger>
        </TabsList>
        <TabsContent value="contacts"><ContactsTab /></TabsContent>
        <TabsContent value="pipeline"><PipelineTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ContactsTab() {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("cards");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [taskFor, setTaskFor] = useState(null);

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ["contacts"],
    queryFn: async () => (await api.get("/contacts")).data,
  });

  const filtered = contacts.filter((c) =>
    (filter === "all" || c.type === filter) &&
    (statusFilter === "all" || c.status === statusFilter) &&
    (c.name.toLowerCase().includes(search.toLowerCase()) || (c.company || "").toLowerCase().includes(search.toLowerCase()))
  );
  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    const av = (a[sortKey] ?? "").toString().toLowerCase();
    const bv = (b[sortKey] ?? "").toString().toLowerCase();
    return av < bv ? -dir : av > bv ? dir : 0;
  });
  const list = view === "list" ? sorted : filtered;
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };
  const SortTh = ({ label, k, className = "" }) => (
    <th className={`px-4 py-3 ${className}`}>
      <button onClick={() => toggleSort(k)} className="flex items-center gap-1 hover:text-foreground" data-testid={`sort-${k}`}>
        {label}<ArrowUpDown className={`h-3 w-3 ${sortKey === k ? "text-accent" : "opacity-40"}`} />
      </button>
    </th>
  );

  const openNew = () => { setForm(EMPTY); setEditOpen(true); };
  const openEdit = (c) => { setForm({ ...c, tags: c.tags || [] }); setEditOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (form.id) await api.put(`/contacts/${form.id}`, payload);
      else await api.post("/contacts", payload);
      toast.success(form.id ? "Contact updated" : "Contact added");
      setEditOpen(false);
      qc.invalidateQueries({ queryKey: ["contacts"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact? This can't be undone.")) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact removed");
      qc.invalidateQueries({ queryKey: ["contacts"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="crm-filter-tabs">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="lead">Prospects</TabsTrigger>
            <TabsTrigger value="client">Clients</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          {view === "list" && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="crm-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {["new", "contacted", "active", "closed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="relative sm:w-56">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="crm-search-input" />
          </div>
          <div className="flex rounded-lg border border-border p-0.5" data-testid="crm-view-toggle">
            <Button size="sm" variant={view === "cards" ? "secondary" : "ghost"} className="h-8 px-2.5" onClick={() => setView("cards")} data-testid="crm-view-cards"><LayoutGrid className="h-4 w-4" /></Button>
            <Button size="sm" variant={view === "list" ? "secondary" : "ghost"} className="h-8 px-2.5" onClick={() => setView("list")} data-testid="crm-view-list"><List className="h-4 w-4" /></Button>
          </div>
          <Button onClick={openNew} data-testid="add-contact-button"><Plus className="mr-2 h-4 w-4" /> Add</Button>
        </div>
      </div>

      {isLoading ? <PageLoader /> : filtered.length === 0 ? (
        <EmptyState icon={Users} title="No contacts yet" description="Add your first prospect or client to start tracking relationships."
          action={<Button onClick={openNew} data-testid="empty-add-contact"><Plus className="mr-2 h-4 w-4" /> Add contact</Button>} testId="crm-empty" />
      ) : view === "list" ? (
        <div className="overflow-x-auto rounded-2xl border border-border" data-testid="contacts-list">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <SortTh label="Name" k="name" />
                <SortTh label="Company" k="company" />
                <SortTh label="Type" k="type" />
                <SortTh label="Status" k="status" />
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.map((c) => (
                <tr key={c.id} className="bg-card hover:bg-secondary/30" data-testid={`contact-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.company || "—"}</td>
                  <td className="px-4 py-3"><Badge variant={c.type === "client" ? "default" : "secondary"} className="capitalize">{c.type === "client" ? "Client" : "Prospect"}</Badge></td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{c.status || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.email ? <a href={`mailto:${c.email}`} className="hover:text-foreground">{c.email}</a> : "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.phone ? <a href={`tel:${c.phone}`} className="hover:text-foreground" data-testid={`contact-phone-${c.id}`}>{c.phone}</a> : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetail(c)} data-testid={`view-contact-${c.id}`}><Sparkles className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setTaskFor(c)} data-testid={`task-contact-${c.id}`} title="Set a task"><CheckSquare className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(c.id)} data-testid={`delete-contact-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((c) => (
            <div key={c.id} className="group rounded-2xl border border-border bg-card p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid={`contact-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-11 w-11"><AvatarFallback className="bg-primary/10 text-primary font-semibold">{c.name.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.company || "—"}</p>
                  </div>
                </div>
                <Badge variant={c.type === "client" ? "default" : "secondary"} className="capitalize">{c.type === "client" ? "Client" : "Prospect"}</Badge>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                {c.phone && <a href={`tel:${c.phone}`} className="flex w-fit items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground" data-testid={`contact-phone-${c.id}`}><Phone className="h-3.5 w-3.5" /> {c.phone}</a>}
                {c.email && <a href={`mailto:${c.email}`} className="flex w-fit items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"><Mail className="h-3.5 w-3.5" /> {c.email}</a>}
              </div>
              {c.tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.tags.map((t) => <span key={t} className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{t}</span>)}
                </div>
              )}
              <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{c.notes || "No notes"}</p>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setDetail(c)} data-testid={`view-contact-${c.id}`}><Sparkles className="mr-1.5 h-3.5 w-3.5" /> AI actions</Button>
                <Button size="sm" variant="ghost" onClick={() => setTaskFor(c)} data-testid={`task-contact-${c.id}`} title="Set a task"><CheckSquare className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => remove(c.id)} data-testid={`delete-contact-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContactDialog open={editOpen} setOpen={setEditOpen} form={form} setForm={setForm} save={save} />
      {detail && <ContactDetail contact={detail} onClose={() => setDetail(null)} qc={qc} refreshUser={refreshUser} />}
      {taskFor && <TaskForContactDialog contact={taskFor} onClose={() => setTaskFor(null)} qc={qc} />}
    </div>
  );
}

function TaskForContactDialog({ contact, onClose, qc }) {
  const [form, setForm] = useState({ title: `Call ${contact.name}`, priority: "medium", due_date: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/tasks", { title: form.title, priority: form.priority, due_date: form.due_date || null, status: "todo", contact_id: contact.id, contact_name: contact.name });
      toast.success(`Task set for ${contact.name}`);
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="contact-task-dialog">
        <DialogHeader>
          <DialogTitle>New task for {contact.name}</DialogTitle>
          <DialogDescription>This task will be linked to {contact.name} and show up in Operations.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Task</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contact-task-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="contact-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="contact-task-due" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.title || saving} data-testid="save-contact-task">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create task</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactDialog({ open, setOpen, form, setForm, save }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="contact-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit contact" : "Add contact"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="contact-name-input" /></div>
            <div className="space-y-1.5"><Label>Company</Label><Input value={form.company} onChange={set("company")} data-testid="contact-company-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={set("email")} data-testid="contact-email-input" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="lead">Prospect</SelectItem><SelectItem value="client">Client</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="contact-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["new", "contacted", "active", "closed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Tags (comma separated)</Label><Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} data-testid="contact-tags-input" /></div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="contact-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.name} data-testid="save-contact-button">Save contact</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactDetail({ contact, onClose, qc, refreshUser }) {
  const [loading, setLoading] = useState("");
  const [aiOutput, setAiOutput] = useState("");
  const [note, setNote] = useState("");

  const runAI = async (kind) => {
    setLoading(kind); setAiOutput("");
    try {
      const urls = {
        summarize: `/contacts/${contact.id}/ai/summarize`,
        email: `/contacts/${contact.id}/ai/draft-email`,
        brief: `/contacts/${contact.id}/ai/brief`,
      };
      const res = await api.post(urls[kind], kind === "summarize" ? undefined : {});
      setAiOutput(res.data.summary || res.data.email || res.data.brief);
      refreshUser();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(""); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/contacts/${contact.id}/interactions`, { note });
    setNote("");
    toast.success("Interaction logged");
    qc.invalidateQueries({ queryKey: ["contacts"] });
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="contact-detail-dialog">
        <DialogHeader><DialogTitle>{contact.name} · {contact.company}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => runAI("brief")} disabled={!!loading} data-testid="ai-client-brief-button">
              {loading === "brief" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FileText className="mr-1.5 h-3.5 w-3.5" />} Client brief
            </Button>
            <Button size="sm" variant="outline" onClick={() => runAI("summarize")} disabled={!!loading} data-testid="ai-summarize-button">
              {loading === "summarize" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />} Summarize lead
            </Button>
            <Button size="sm" variant="outline" onClick={() => runAI("email")} disabled={!!loading} data-testid="ai-draft-email-button">
              {loading === "email" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1.5 h-3.5 w-3.5" />} Draft email
            </Button>
          </div>
          {loading === "brief" && <p className="text-xs text-muted-foreground" data-testid="brief-loading-hint">Gathering this client's projects, tasks, invoices, files, and activity…</p>}
          {aiOutput && <div className="rounded-xl border border-accent/30 bg-accent/5 p-4 text-sm whitespace-pre-wrap" data-testid="ai-output">{aiOutput}</div>}

          <div>
            <p className="mb-2 text-sm font-semibold">Interaction history</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(contact.interactions || []).length ? contact.interactions.map((i) => (
                <div key={i.id} className="rounded-lg border border-border p-2.5 text-sm">
                  <p>{i.note}</p><p className="mt-0.5 text-xs text-muted-foreground">{new Date(i.at).toLocaleString()}</p>
                </div>
              )) : <p className="text-sm text-muted-foreground">No interactions logged yet.</p>}
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a call, email, meeting..." data-testid="interaction-input" />
              <Button size="icon" onClick={addNote} data-testid="add-interaction-button"><MessageSquarePlus className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const DEAL_EMPTY = { title: "", contact_id: "", contact_name: "", value: 0, stage: "new", expected_close: "", notes: "" };

function PipelineTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEAL_EMPTY);
  const [manageOpen, setManageOpen] = useState(false);

  const { data: deals = [], isLoading } = useQuery({ queryKey: ["deals"], queryFn: async () => (await api.get("/deals")).data });
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/contacts")).data });
  const { data: stagesData } = useQuery({ queryKey: ["pipeline-stages"], queryFn: async () => (await api.get("/pipeline/stages")).data });
  const stages = stagesData?.stages || [];

  const openNew = () => { setForm({ ...DEAL_EMPTY, stage: stages[0]?.id || "new" }); setOpen(true); };
  const openEdit = (d) => { setForm({ ...d, contact_id: d.contact_id || "", expected_close: d.expected_close || "" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, value: Number(form.value) || 0, contact_id: form.contact_id || null };
      if (form.id) await api.put(`/deals/${form.id}`, payload);
      else await api.post("/deals", payload);
      toast.success(form.id ? "Opportunity updated" : "Opportunity added");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const move = async (id, stage) => {
    await api.patch(`/deals/${id}/stage`, { stage });
    qc.invalidateQueries({ queryKey: ["deals"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const remove = async (id) => { await api.delete(`/deals/${id}`); qc.invalidateQueries({ queryKey: ["deals"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  if (isLoading) return <PageLoader />;

  const openIds = stages.filter((s) => s.type === "open").map((s) => s.id);
  const wonIds = stages.filter((s) => s.type === "won").map((s) => s.id);
  const openValue = deals.filter((d) => openIds.includes(d.stage)).reduce((s, d) => s + (d.value || 0), 0);
  const wonValue = deals.filter((d) => wonIds.includes(d.stage)).reduce((s, d) => s + (d.value || 0), 0);

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-6 text-sm">
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Open value</p><p className="font-display text-xl font-bold">{money(openValue)}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Won</p><p className="font-display text-xl font-bold text-accent">{money(wonValue)}</p></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setManageOpen(true)} data-testid="customize-stages-button"><Settings2 className="mr-2 h-4 w-4" /> Customize stages</Button>
          <Button onClick={openNew} data-testid="add-deal-button"><Plus className="mr-2 h-4 w-4" /> Add opportunity</Button>
        </div>
      </div>

      {deals.length === 0 ? (
        <EmptyState icon={Handshake} title="No opportunities yet" description="Track an opportunity from first conversation all the way to won."
          action={<Button onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Add your first opportunity</Button>} testId="pipeline-empty" />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {stages.map((col, colIdx) => {
            const items = deals.filter((d) => d.stage === col.id);
            const colValue = items.reduce((s, d) => s + (d.value || 0), 0);
            return (
              <div key={col.id} className="w-[250px] shrink-0 rounded-2xl border border-border bg-secondary/30 p-3"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { const id = e.dataTransfer.getData("id"); if (id) move(id, col.id); }}
                data-testid={`pipeline-column-${col.id}`}>
                <div className="mb-3 px-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${stageColor(col, colIdx)}`} /><span className="text-sm font-semibold">{col.label}</span></div>
                    <span className="rounded-full bg-background px-2 text-xs text-muted-foreground">{items.length}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{money(colValue)}</p>
                </div>
                <div className="space-y-2.5 min-h-[60px]">
                  {items.map((d) => (
                    <div key={d.id} draggable onDragStart={(e) => e.dataTransfer.setData("id", d.id)}
                      className="group cursor-grab rounded-xl border border-border bg-card p-3 active:cursor-grabbing" data-testid={`deal-card-${d.id}`}>
                      <div className="flex items-start gap-1.5">
                        <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium leading-snug">{d.title}</p>
                          {d.contact_name && <p className="mt-0.5 text-xs text-muted-foreground">{d.contact_name}</p>}
                          <p className="mt-1 font-display text-sm font-bold">{money(d.value)}</p>
                          {d.expected_close && <p className="mt-0.5 text-xs text-muted-foreground">Close {d.expected_close}</p>}
                          <div className="mt-2 flex items-center gap-1">
                            <Select value={d.stage} onValueChange={(v) => move(d.id, v)}>
                              <SelectTrigger className="h-7 flex-1 text-xs" data-testid={`deal-stage-${d.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(d)} data-testid={`edit-deal-${d.id}`}><MessageSquarePlus className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remove(d.id)} data-testid={`delete-deal-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <p className="py-3 text-center text-xs text-muted-foreground">Drop deals here</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="deal-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Edit opportunity" : "Add opportunity"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>What's the opportunity?</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Website project for Acme" data-testid="deal-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact</Label>
                <Select value={form.contact_id || "none"} onValueChange={(v) => { const c = contacts.find((x) => x.id === v); setForm({ ...form, contact_id: v === "none" ? "" : v, contact_name: c ? c.name : "" }); }}>
                  <SelectTrigger data-testid="deal-contact-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Value ($)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="deal-value-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Expected close</Label><Input type="date" value={form.expected_close} onChange={(e) => setForm({ ...form, expected_close: e.target.value })} data-testid="deal-close-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="deal-notes-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.title} data-testid="save-deal-button">Save deal</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {manageOpen && <StageManager stages={stages} onClose={() => setManageOpen(false)} qc={qc} />}
    </div>
  );
}

function StageManager({ stages, onClose, qc }) {
  const [rows, setRows] = useState(stages.map((s) => ({ ...s })));
  const [saving, setSaving] = useState(false);

  const setRow = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addRow = () => setRows((r) => [...r, { id: "", label: "", type: "open" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const moveRow = (i, dir) => setRows((r) => {
    const j = i + dir;
    if (j < 0 || j >= r.length) return r;
    const copy = [...r];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const save = async () => {
    const cleaned = rows.map((r) => ({ id: r.id || undefined, label: r.label.trim(), type: r.type })).filter((r) => r.label);
    if (cleaned.length === 0) { toast.error("Add at least one stage"); return; }
    setSaving(true);
    try {
      await api.put("/pipeline/stages", { stages: cleaned });
      toast.success("Stages updated");
      qc.invalidateQueries({ queryKey: ["pipeline-stages"] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="stage-manager-dialog">
        <DialogHeader><DialogTitle>Customize your stages</DialogTitle></DialogHeader>
        <p className="-mt-1 text-sm text-muted-foreground">Rename, reorder, add or remove stages to match how your business works.</p>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto py-1">
          {rows.map((row, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`stage-row-${i}`}>
              <div className="flex flex-col">
                <button type="button" onClick={() => moveRow(i, -1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === 0} data-testid={`stage-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => moveRow(i, 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === rows.length - 1} data-testid={`stage-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
              <Input value={row.label} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Stage name" className="flex-1" data-testid={`stage-label-${i}`} />
              <Select value={row.type} onValueChange={(v) => setRow(i, "type", v)}>
                <SelectTrigger className="w-[130px]" data-testid={`stage-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => removeRow(i)} data-testid={`stage-remove-${i}`}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} className="w-full" data-testid="add-stage-button"><Plus className="mr-1.5 h-3.5 w-3.5" /> Add stage</Button>
        </div>
        <p className="text-xs text-muted-foreground">Tip: mark a stage as "Won" or "Lost" so your dashboard totals stay accurate.</p>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-stages-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save stages</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

